import React, { useEffect, useState, useRef } from 'react';
import { Play, Pause, Volume2, VolumeX, Maximize, Rewind, FastForward, Settings, Check } from 'lucide-react';
interface VideoPlayerProps {
  thumbnailUrl?: string;
  autoPlay?: boolean;
  onProgress?: (progress: number) => void;
  videoUrl?: string;
  enableFullscreen?: boolean;
}
export function VideoPlayer({
  thumbnailUrl = 'https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?w=800&h=450&fit=crop&crop=faces&q=80',
  autoPlay = false,
  onProgress,
  videoUrl,
  enableFullscreen = true
}: VideoPlayerProps) {
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [isMuted, setIsMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [videoQuality, setVideoQuality] = useState('720p');
  const [isDragging, setIsDragging] = useState(false);
  const videoPlayerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const settingsRef = useRef<HTMLDivElement>(null);
  // Initialize video element
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.addEventListener('timeupdate', handleTimeUpdate);
      videoRef.current.addEventListener('loadedmetadata', handleLoadedMetadata);
      videoRef.current.addEventListener('ended', handleVideoEnded);
      return () => {
        if (videoRef.current) {
          videoRef.current.removeEventListener('timeupdate', handleTimeUpdate);
          videoRef.current.removeEventListener('loadedmetadata', handleLoadedMetadata);
          videoRef.current.removeEventListener('ended', handleVideoEnded);
        }
      };
    }
  }, []);
  // Handle video time update
  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const current = videoRef.current.currentTime;
      const total = videoRef.current.duration;
      const progressPercent = current / total * 100;
      setCurrentTime(current);
      setProgress(progressPercent);
      if (onProgress) {
        onProgress(progressPercent);
      }
    }
  };
  // Handle video metadata loaded
  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };
  // Handle video ended
  const handleVideoEnded = () => {
    setIsPlaying(false);
    setProgress(100);
  };
  // Format time to MM:SS
  const formatTime = (timeInSeconds: number) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };
  // Handle play/pause toggle
  const togglePlay = () => {
    if (videoRef.current && videoUrl) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };
  // Handle mute toggle
  const toggleMute = () => {
    if (videoRef.current && videoUrl) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };
  // Handle seeking - when user clicks on progress bar
  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    if (progressBarRef.current && videoRef.current && videoUrl && !isNaN(duration) && duration > 0) {
      const rect = progressBarRef.current.getBoundingClientRect();
      const offsetX = e.clientX - rect.left;
      const newProgress = offsetX / rect.width * 100;
      const newTime = newProgress / 100 * duration;
      if (!isNaN(newTime) && isFinite(newTime)) {
        videoRef.current.currentTime = newTime;
        setProgress(newProgress);
        setCurrentTime(newTime);
      }
    }
  };
  // Handle drag start on progress bar
  const handleDragStart = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!videoUrl || isNaN(duration) || duration <= 0) return;
    setIsDragging(true);
    handleSeek(e);
    // Add event listeners for drag and drag end
    document.addEventListener('mousemove', handleDrag);
    document.addEventListener('mouseup', handleDragEnd);
  };
  // Handle dragging on progress bar
  const handleDrag = (e: MouseEvent) => {
    if (isDragging && progressBarRef.current && videoRef.current && videoUrl && !isNaN(duration) && duration > 0) {
      const rect = progressBarRef.current.getBoundingClientRect();
      const offsetX = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
      const newProgress = offsetX / rect.width * 100;
      const newTime = newProgress / 100 * duration;
      if (!isNaN(newTime) && isFinite(newTime)) {
        videoRef.current.currentTime = newTime;
        setProgress(newProgress);
        setCurrentTime(newTime);
      }
    }
  };
  // Handle drag end
  const handleDragEnd = () => {
    setIsDragging(false);
    document.removeEventListener('mousemove', handleDrag);
    document.removeEventListener('mouseup', handleDragEnd);
  };
  // Handle seeking forward or backward by seconds
  const handleSkip = (seconds: number) => {
    if (videoRef.current && videoUrl && !isNaN(videoRef.current.duration) && videoRef.current.duration > 0) {
      // Get current time directly from the video element
      const currentVideoTime = videoRef.current.currentTime;
      const videoDuration = videoRef.current.duration;
      // Only proceed if we have valid values
      if (!isNaN(currentVideoTime) && !isNaN(videoDuration) && isFinite(currentVideoTime) && isFinite(videoDuration)) {
        // Calculate new time, ensuring it's within valid bounds
        const newTime = Math.max(0, Math.min(currentVideoTime + seconds, videoDuration));
        // Double-check the calculated time is valid before setting
        if (!isNaN(newTime) && isFinite(newTime)) {
          videoRef.current.currentTime = newTime;
          setCurrentTime(newTime);
          setProgress(newTime / videoDuration * 100);
        }
      }
    }
  };
  // Handle fullscreen toggle - improved implementation
  const toggleFullscreen = () => {
    if (!videoPlayerRef.current) return;
    try {
      if (!document.fullscreenElement) {
        // Enter fullscreen
        if (videoPlayerRef.current.requestFullscreen) {
          videoPlayerRef.current.requestFullscreen();
        } else if ((videoPlayerRef.current as any).webkitRequestFullscreen) {
          ;
          (videoPlayerRef.current as any).webkitRequestFullscreen();
        } else if ((videoPlayerRef.current as any).msRequestFullscreen) {
          ;
          (videoPlayerRef.current as any).msRequestFullscreen();
        }
        setIsFullscreen(true);
      } else {
        // Exit fullscreen
        if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if ((document as any).webkitExitFullscreen) {
          ;
          (document as any).webkitExitFullscreen();
        } else if ((document as any).msExitFullscreen) {
          ;
          (document as any).msExitFullscreen();
        }
        setIsFullscreen(false);
      }
    } catch (error) {
      console.error('Fullscreen error:', error);
    }
  };
  // Listen for fullscreen change events
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);
  // Close settings menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (settingsRef.current && !settingsRef.current.contains(event.target as Node)) {
        setShowSettingsMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // Update video playback speed when changed
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = playbackSpeed;
    }
  }, [playbackSpeed]);
  return <div className="relative p-[1px] rounded-xl overflow-hidden bg-gradient-to-r from-purple-600 to-indigo-600" data-id="element-1289">
      <div className="relative bg-black/90 rounded-xl" data-id="element-1290">
        {/* Video Container */}
        <div ref={videoPlayerRef} className="relative aspect-video bg-black" data-id="element-1291">
          {/* Actual video element */}
          <video ref={videoRef} className="w-full h-full object-cover" src={videoUrl} preload="metadata" muted={isMuted} autoPlay={autoPlay} data-id="element-1292" />
          {!videoUrl && <div className="absolute inset-0 flex items-center justify-center" data-id="element-1293">
              <img src={thumbnailUrl} alt="Video thumbnail" className="w-full h-full object-cover opacity-70" data-id="element-1294" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" data-id="element-1295"></div>
            </div>}
          {/* Video Controls Overlay */}
          <div className="absolute inset-0 flex flex-col justify-end p-4" data-id="element-1296">
            <div className="mb-2" data-id="element-1297">
              {/* Progress bar - now interactive */}
              <div ref={progressBarRef} className={`relative h-1 bg-white/30 rounded-full ${videoUrl && duration > 0 ? 'cursor-pointer' : 'cursor-default'}`} onClick={videoUrl && duration > 0 ? handleSeek : undefined} onMouseDown={videoUrl && duration > 0 ? handleDragStart : undefined} data-id="element-1298">
                <div className="absolute h-full bg-gradient-to-r from-purple-600 to-indigo-600 rounded-full" style={{
                width: `${progress}%`
              }} data-id="element-1299"></div>
                <div className="absolute h-3 w-3 bg-white rounded-full -mt-1 shadow-sm" style={{
                left: `${progress}%`
              }} data-id="element-1300"></div>
              </div>
            </div>
            <div className="flex justify-between items-center" data-id="element-1301">
              <div className="flex items-center gap-3" data-id="element-1302">
                <button className={`p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors ${!videoUrl ? 'opacity-50 cursor-not-allowed' : ''}`} title="10 saniye geri" onClick={() => videoUrl && handleSkip(-10)} disabled={!videoUrl} data-id="element-1303">
                  <Rewind className="w-5 h-5" data-id="element-1304" />
                </button>
                <button onClick={togglePlay} className={`p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors ${!videoUrl ? 'opacity-50 cursor-not-allowed' : ''}`} disabled={!videoUrl} data-id="element-1305">
                  {isPlaying ? <Pause className="w-5 h-5" data-id="element-1306" /> : <Play className="w-5 h-5" data-id="element-1307" />}
                </button>
                <button className={`p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors ${!videoUrl ? 'opacity-50 cursor-not-allowed' : ''}`} title="10 saniye ileri" onClick={() => videoUrl && handleSkip(10)} disabled={!videoUrl} data-id="element-1308">
                  <FastForward className="w-5 h-5" data-id="element-1309" />
                </button>
                <button onClick={toggleMute} className={`p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors ${!videoUrl ? 'opacity-50 cursor-not-allowed' : ''}`} disabled={!videoUrl} data-id="element-1310">
                  {isMuted ? <VolumeX className="w-5 h-5" data-id="element-1311" /> : <Volume2 className="w-5 h-5" data-id="element-1312" />}
                </button>
                <span className="text-sm ml-2" data-id="element-1313">
                  {formatTime(currentTime)} / {formatTime(duration || 83)}{' '}
                  {/* Default to 1:23 if duration not available */}
                </span>
              </div>
              <div className="flex items-center gap-2" data-id="element-1314">
                {/* Settings button with dropdown */}
                <div className="relative" ref={settingsRef} data-id="element-1315">
                  <button className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors" onClick={() => setShowSettingsMenu(!showSettingsMenu)} data-id="element-1316">
                    <Settings className="w-5 h-5" data-id="element-1317" />
                  </button>
                  {showSettingsMenu && <div className="absolute bottom-12 right-0 bg-black/95 border border-gray-800 rounded-lg shadow-lg p-2 min-w-[200px] z-50" data-id="element-1318">
                      {/* Playback Speed */}
                      <div className="p-2" data-id="element-1319">
                        <p className="text-sm text-gray-400 mb-2" data-id="element-1320">
                          Oynatma Hızı
                        </p>
                        <div className="grid grid-cols-4 gap-1" data-id="element-1321">
                          {[0.5, 1, 1.5, 2].map(speed => <button key={speed} className={`px-2 py-1 text-sm rounded ${playbackSpeed === speed ? 'bg-purple-600 text-white' : 'bg-white/10 text-gray-300 hover:bg-white/20'}`} onClick={() => setPlaybackSpeed(speed)} data-id="element-1322">
                              {speed}x
                            </button>)}
                        </div>
                      </div>
                      {/* Video Quality */}
                      <div className="p-2 border-t border-gray-800 mt-1" data-id="element-1323">
                        <p className="text-sm text-gray-400 mb-2" data-id="element-1324">Kalite</p>
                        <div className="space-y-1" data-id="element-1325">
                          {['480p', '720p', '1080p'].map(quality => <button key={quality} className={`flex items-center gap-2 w-full px-3 py-1.5 text-sm rounded text-left ${videoQuality === quality ? 'bg-purple-600 text-white' : 'hover:bg-white/10 text-gray-300'}`} onClick={() => setVideoQuality(quality)} data-id="element-1326">
                              {quality}
                              {videoQuality === quality && <Check className="w-4 h-4 ml-auto" data-id="element-1327" />}
                            </button>)}
                        </div>
                      </div>
                    </div>}
                </div>
                {/* Fullscreen button */}
                {enableFullscreen && <button className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors" onClick={toggleFullscreen} data-id="element-1328">
                    <Maximize className="w-5 h-5" data-id="element-1329" />
                  </button>}
              </div>
            </div>
          </div>
          {/* Center Play Button */}
          {!isPlaying && <button onClick={togglePlay} className="absolute inset-0 flex items-center justify-center" data-id="element-1330">
              <div className="p-4 rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/30 transition-colors" data-id="element-1331">
                <Play className="w-10 h-10" fill="white" data-id="element-1332" />
              </div>
            </button>}
        </div>
      </div>
    </div>;
}